import { WebSubscriptionCheckoutSessionPayload } from "../api/paymentApi";
import {
  isOptionalPositiveFiniteNumber,
  isOptionalString,
  isPositiveFiniteNumber,
} from "./payloadValidators";

const PENDING_GUEST_STRIPE_SUBSCRIPTION_SETUP_KEY =
  "pendingGuestStripeSubscriptionSetup";

const PENDING_GUEST_STRIPE_SUBSCRIPTION_SETUP_TTL_MS = 30 * 60 * 1000;

type PendingGuestStripeSubscriptionSetup = {
  payload: WebSubscriptionCheckoutSessionPayload;
  createdAt: number;
  completedSetupIntentId?: string;
  /**
   * Server-issued single-use nonce for the SetupIntent. The finalize endpoint
   * compares this to the value stamped into Stripe metadata at creation time.
   * Without it, a leaked setup id (Referer, history) can be claimed by any
   * logged-in account.
   */
  clientNonce?: string;
};

export type PendingGuestStripeSubscriptionSetupState =
  PendingGuestStripeSubscriptionSetup;

function isValidPayloadShape(
  payload: unknown,
): payload is WebSubscriptionCheckoutSessionPayload {
  if (!payload || typeof payload !== "object") {
    return false;
  }
  const candidate = payload as Record<string, unknown>;
  if (!isPositiveFiniteNumber(candidate.accessTokenTypeId)) {
    return false;
  }
  if (candidate.incentiveCode !== null && !isOptionalString(candidate.incentiveCode)) {
    return false;
  }
  if (!isOptionalString(candidate.userOfferUuid)) {
    return false;
  }
  if (!isOptionalString(candidate.context)) {
    return false;
  }
  if (!isOptionalPositiveFiniteNumber(candidate.welcomeGiftId)) {
    return false;
  }
  return true;
}

function readPendingState():
  | PendingGuestStripeSubscriptionSetup
  | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  const serialized = window.sessionStorage.getItem(
    PENDING_GUEST_STRIPE_SUBSCRIPTION_SETUP_KEY,
  );
  if (!serialized) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(serialized) as PendingGuestStripeSubscriptionSetup;
    if (
      !parsed ||
      typeof parsed !== "object" ||
      typeof parsed.createdAt !== "number" ||
      !isValidPayloadShape(parsed.payload)
    ) {
      clearPendingGuestStripeSubscriptionSetup();
      return undefined;
    }

    if (
      Date.now() - parsed.createdAt >
      PENDING_GUEST_STRIPE_SUBSCRIPTION_SETUP_TTL_MS
    ) {
      clearPendingGuestStripeSubscriptionSetup();
      return undefined;
    }

    return parsed;
  } catch {
    clearPendingGuestStripeSubscriptionSetup();
    return undefined;
  }
}

function writePendingState(state: PendingGuestStripeSubscriptionSetup) {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.setItem(
    PENDING_GUEST_STRIPE_SUBSCRIPTION_SETUP_KEY,
    JSON.stringify(state),
  );
}

export function persistPendingGuestStripeSubscriptionSetup(
  payload: WebSubscriptionCheckoutSessionPayload,
  clientNonce?: string,
) {
  writePendingState({
    payload,
    createdAt: Date.now(),
    clientNonce,
  });
}

export function attachPendingGuestStripeSubscriptionSetupNonce(
  clientNonce: string,
) {
  const state = readPendingState();
  if (!state) {
    return;
  }
  writePendingState({
    ...state,
    clientNonce,
  });
}

export function updatePendingGuestStripeSubscriptionSetupPayload(
  payload: WebSubscriptionCheckoutSessionPayload,
) {
  const state = readPendingState();
  if (!state) {
    persistPendingGuestStripeSubscriptionSetup(payload);
    return;
  }

  writePendingState({
    ...state,
    payload,
  });
}

export function markPendingGuestStripeSubscriptionSetupIntentCompleted(
  setupIntentId: string,
) {
  const state = readPendingState();
  if (!state) {
    return;
  }

  writePendingState({
    ...state,
    completedSetupIntentId: setupIntentId,
  });
}

export function clearPendingGuestStripeSubscriptionSetup() {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.removeItem(
    PENDING_GUEST_STRIPE_SUBSCRIPTION_SETUP_KEY,
  );
}

export function getPendingGuestStripeSubscriptionSetup():
  | PendingGuestStripeSubscriptionSetupState
  | undefined {
  return readPendingState();
}
